import { useEffect, useState } from 'react';

// material-ui
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// third-party
import * as Yup from 'yup';
import { Formik } from 'formik';

// project imports
import useAuth from 'hooks/useAuth';
import PinKeypad from 'components/PinKeypad';
import { PIN_LENGTH, PIN_MESSAGE, PIN_PATTERN } from 'utils/pinConfig';

// ============================|| PIN REQUIRED MODAL ||============================ //
// Blocking (no close button, no backdrop/Escape dismiss) - shown when the
// session user holds the reserved 'pin' module role (pin_required, from
// GET /auth/me) but hasn't set a PIN yet (has_pin, GET /account/pin).
// 2026-08-21, "role a skupiny" delegation design: PIN becomes mandatory for
// a user the moment an admin grants them that role, enforced here on their
// very next authenticated page load. Uses the existing direct POST
// /account/pin (immediate set, no e-mail round trip) - the same endpoint
// UserSettingsAccordion.jsx's older same-session flow used before "Změnit
// PIN" switched to the e-mail-confirmed flow; that switch only affected
// CHANGING an existing PIN, not this first-time mandatory setup.

export default function PinRequiredModal() {
  const { user, getPinStatus, setPin } = useAuth();
  const [checked, setChecked] = useState(false);
  const [required, setRequired] = useState(false);
  const [activeField, setActiveField] = useState('pin');

  useEffect(() => {
    if (!user?.pin_required) {
      setChecked(true);
      setRequired(false);
      return;
    }

    let cancelled = false;
    getPinStatus()
      .then((data) => {
        if (!cancelled) setRequired(!data.has_pin);
      })
      .catch(() => {
        if (!cancelled) setRequired(false);
      })
      .finally(() => {
        if (!cancelled) setChecked(true);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.pin_required, getPinStatus]);

  if (!checked || !required) {
    return null;
  }

  return (
    <Dialog open disableEscapeKeyDown maxWidth="xs" fullWidth>
      <DialogTitle>Nastavte si PIN ({PIN_LENGTH} číslic)</DialogTitle>
      <DialogContent>
        <Stack sx={{ gap: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Váš účet vyžaduje nastavení PIN kódu, než budete moci pokračovat. PIN budete potřebovat pro potvrzování vybraných akcí.
          </Typography>
          <Formik
            initialValues={{ pin: '', pinConfirmation: '', submit: null }}
            validationSchema={Yup.object().shape({
              pin: Yup.string().matches(PIN_PATTERN, PIN_MESSAGE).required(PIN_MESSAGE),
              pinConfirmation: Yup.string()
                .oneOf([Yup.ref('pin')], 'PIN a jeho potvrzení se musí shodovat.')
                .required(PIN_MESSAGE)
            })}
            onSubmit={async (values, { setErrors, setSubmitting }) => {
              try {
                await setPin(values.pin, values.pinConfirmation);
                setSubmitting(false);
                setRequired(false);
              } catch (error) {
                setErrors({ submit: error.message || 'PIN se nepodařilo nastavit.' });
                setSubmitting(false);
              }
            }}
          >
            {({ errors, handleSubmit, isSubmitting, setFieldTouched, setFieldValue, touched, values }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Stack sx={{ gap: 2 }}>
                  {/* Vlastní číselník, žádný nativní <input> (2026-08-23, na
                      výslovné přání uživatele) - úplně odstraňuje OS
                      klávesnici i s ní spojené autofill/"uložit heslo"
                      nabídky, které na mobilu uměly zakrýt celé pole (dřív
                      řešeno jen autoComplete="one-time-code", tohle je
                      důkladnější náhrada, viz components/PinKeypad.jsx). */}
                  <Typography variant="subtitle2" sx={{ textAlign: 'center' }}>
                    {activeField === 'pin' ? 'Zadejte PIN' : 'Potvrďte znovu PIN'}
                  </Typography>
                  <PinKeypad
                    value={activeField === 'pin' ? values.pin : values.pinConfirmation}
                    onChange={(next) => {
                      setFieldValue(activeField, next);
                      setFieldTouched(activeField, true, false);
                      if (activeField === 'pin' && next.length === PIN_LENGTH) {
                        setActiveField('pinConfirmation');
                      }
                    }}
                  />
                  {touched.pin && errors.pin && <FormHelperText error>{errors.pin}</FormHelperText>}
                  {touched.pinConfirmation && errors.pinConfirmation && <FormHelperText error>{errors.pinConfirmation}</FormHelperText>}
                  {errors.submit && <Alert severity="error">{errors.submit}</Alert>}
                  <Button disabled={isSubmitting} fullWidth size="large" type="submit" variant="contained" sx={{ textTransform: 'none' }}>
                    Nastavit PIN
                  </Button>
                </Stack>
              </form>
            )}
          </Formik>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
